import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RiseIn, SplitWords } from "@/components/site/Reveal";
import { DOCTOR, LOCATIONS, SLOTS } from "@/lib/data";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initial = {
  name: "",
  phone: "",
  email: "",
  age: "",
  concern: "",
  date: null,
  slot: "",
  locationId: LOCATIONS[0].id,
};

export default function Booking() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const location = useMemo(
    () => LOCATIONS.find((l) => l.id === form.locationId) || LOCATIONS[0],
    [form.locationId]
  );

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildWaLink = () => {
    const parts = [
      `*New Appointment Request — Dr. Vikas Matai*`,
      ``,
      `*Name:* ${form.name}`,
      `*Phone:* ${form.phone}`,
      form.email ? `*Email:* ${form.email}` : null,
      form.age ? `*Age:* ${form.age}` : null,
      ``,
      `*Preferred Date:* ${form.date ? format(form.date, "EEEE, d MMMM yyyy") : "—"}`,
      `*Preferred Slot:* ${form.slot}`,
      `*Location:* ${location.name}`,
      `*Address:* ${location.address}`,
      ``,
      form.concern ? `*Reason for consult:*\n${form.concern}` : null,
      ``,
      `— Sent from drvikasmatai.in`,
    ].filter(Boolean);
    const text = encodeURIComponent(parts.join("\n"));
    return `https://wa.me/${DOCTOR.whatsapp}?text=${text}`;
  };

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!/^\+?\d[\d\s-]{7,}$/.test(form.phone)) return "Please enter a valid phone number.";
    if (!form.date) return "Please choose a date.";
    if (!form.slot) return "Please pick a time slot.";
    if (!form.locationId) return "Please choose a location.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err, { data: { "data-testid": "form-error" } });
      return;
    }
    setSubmitting(true);
    try {
      // Best-effort backend log — do not block WhatsApp if it fails.
      await fetch(`${BACKEND_URL}/api/appointments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email || null,
          age: form.age ? Number(form.age) : null,
          concern: form.concern || null,
          preferred_date: form.date ? form.date.toISOString().slice(0, 10) : null,
          preferred_slot: form.slot,
          location_id: location.id,
          location_name: location.name,
        }),
      }).catch(() => null);

      const link = buildWaLink();
      toast.success("Opening WhatsApp to confirm your booking…");
      window.open(link, "_blank", "noopener,noreferrer");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-36 pb-8" data-testid="booking-page">
      <section className="px-6 lg:px-12">
        <div className="mx-auto max-w-[1600px] grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-8">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brass-500 mb-6">
              Chapter 04 · Reserve a consultation
            </div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-[-0.02em] text-navy-600">
              <SplitWords text="Choose a slot." />
              <br />
              <span className="italic pl-6 md:pl-16">
                <SplitWords text="Confirm on WhatsApp." delay={0.15} />
              </span>
            </h1>
          </div>
          <RiseIn className="col-span-12 lg:col-span-4 lg:pt-6" delay={0.2}>
            <p className="text-sm md:text-base text-navy-600/75 leading-relaxed max-w-sm">
              Fill the details below. When you submit, WhatsApp will open with
              your booking already written out — one tap sends it to Dr. Matai&apos;s
              office for confirmation.
            </p>
          </RiseIn>
        </div>
      </section>

      {/* Form */}
      <section className="px-6 lg:px-12 mt-20">
        <div className="mx-auto max-w-[1600px] grid grid-cols-12 gap-8 lg:gap-16 items-start">
          <form
            onSubmit={submit}
            className="col-span-12 lg:col-span-7"
            data-testid="booking-form"
          >
            <FieldRow label="01 · Full name">
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Priya Sharma"
                className="luxe-input w-full py-3 text-navy-600 placeholder:text-navy-600/40"
                data-testid="input-name"
              />
            </FieldRow>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <FieldRow label="02 · Phone (WhatsApp)">
                <input
                  type="tel"
                  required
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="+91 98765 43210"
                  className="luxe-input w-full py-3 text-navy-600 placeholder:text-navy-600/40"
                  data-testid="input-phone"
                />
              </FieldRow>
              <FieldRow label="03 · Email (optional)">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="priya@example.com"
                  className="luxe-input w-full py-3 text-navy-600 placeholder:text-navy-600/40"
                  data-testid="input-email"
                />
              </FieldRow>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <FieldRow label="04 · Age">
                <input
                  type="number"
                  min="1"
                  max="120"
                  value={form.age}
                  onChange={(e) => set("age", e.target.value)}
                  placeholder="34"
                  className="luxe-input w-full py-3 text-navy-600 placeholder:text-navy-600/40"
                  data-testid="input-age"
                />
              </FieldRow>
              <FieldRow label="05 · Preferred date">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="luxe-input w-full py-3 text-left text-navy-600 flex items-center justify-between"
                      data-testid="date-trigger"
                    >
                      {form.date ? format(form.date, "EEE, d MMM yyyy") : "Pick a date"}
                      <CalendarIcon size={14} className="text-navy-600/60" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="p-0 bg-beige-100 border border-navy-600/20 rounded-none"
                  >
                    <Calendar
                      mode="single"
                      selected={form.date}
                      onSelect={(d) => set("date", d)}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                      data-testid="date-calendar"
                    />
                  </PopoverContent>
                </Popover>
              </FieldRow>
            </div>

            <FieldRow label="06 · Preferred time">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                {SLOTS.map((s) => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => set("slot", s)}
                    data-testid={`slot-${s.replace(/\s+/g, "-")}`}
                    className={`py-3 text-[11px] uppercase tracking-[0.22em] border transition-colors ${
                      form.slot === s
                        ? "bg-navy-600 text-beige-100 border-navy-600"
                        : "bg-transparent text-navy-600 border-navy-600/30 hover:border-navy-600"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </FieldRow>

            <FieldRow label="07 · Location">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                {LOCATIONS.map((l) => (
                  <button
                    type="button"
                    key={l.id}
                    onClick={() => set("locationId", l.id)}
                    data-testid={`location-${l.id}`}
                    className={`text-left p-5 border transition-colors ${
                      form.locationId === l.id
                        ? "bg-navy-600 text-beige-100 border-navy-600"
                        : "bg-transparent text-navy-600 border-navy-600/25 hover:border-navy-600"
                    }`}
                  >
                    <div className="font-serif text-lg tracking-tight leading-tight">
                      {l.name}
                    </div>
                    <div
                      className={`mt-3 text-[11px] leading-relaxed ${
                        form.locationId === l.id
                          ? "text-beige-100/80"
                          : "text-navy-600/60"
                      }`}
                    >
                      {l.address}
                    </div>
                  </button>
                ))}
              </div>
            </FieldRow>

            <FieldRow label="08 · Reason for consult (optional)">
              <textarea
                value={form.concern}
                onChange={(e) => set("concern", e.target.value)}
                rows={3}
                placeholder="Recurrent right-sided abdominal pain since March…"
                className="luxe-input w-full py-3 text-navy-600 placeholder:text-navy-600/40 resize-none"
                data-testid="input-concern"
              />
            </FieldRow>

            <motion.button
              type="submit"
              disabled={submitting}
              whileHover={{ x: 4 }}
              className="mt-12 group inline-flex items-center gap-3 bg-navy-600 text-beige-100 px-8 py-5 text-[11px] uppercase tracking-[0.28em] hover:bg-navy-700 transition-colors disabled:opacity-60"
              data-testid="booking-submit-button"
            >
              {submitting ? "Preparing WhatsApp…" : "Confirm on WhatsApp"}
              <ArrowUpRight
                size={16}
                className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </motion.button>
            <p className="mt-4 text-[10px] uppercase tracking-[0.24em] text-navy-600/50">
              WhatsApp opens in a new tab with your details filled in.
            </p>
          </form>

          {/* Summary panel */}
          <aside className="col-span-12 lg:col-span-5 lg:sticky lg:top-32" data-testid="booking-summary">
            <div className="bg-navy-600 text-beige-100 p-8 lg:p-10">
              <div className="text-[10px] uppercase tracking-[0.3em] text-brass-400 mb-6">
                Your reservation
              </div>
              <SummaryRow label="Name" value={form.name || "—"} />
              <SummaryRow label="Phone" value={form.phone || "—"} />
              <SummaryRow
                label="Date"
                value={form.date ? format(form.date, "EEE, d MMM yyyy") : "—"}
              />
              <SummaryRow label="Time" value={form.slot || "—"} />
              <SummaryRow label="Clinic" value={location.name} />

              <div className="mt-8 pt-8 border-t border-beige-100/15 text-xs text-beige-100/70 leading-relaxed">
                A confirmation from Dr. Matai&apos;s office will follow on the same
                WhatsApp thread. If a slot is unavailable, we will propose the
                next best time.
              </div>
            </div>

            <div className="mt-6 text-[10px] uppercase tracking-[0.24em] text-navy-600/60">
              Or WhatsApp directly · {DOCTOR.phoneDisplay}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div className="mt-10 first:mt-0">
      <div className="text-[10px] uppercase tracking-[0.28em] text-navy-600/60 mb-3">
        {label}
      </div>
      {children}
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="py-3 border-b border-beige-100/10 grid grid-cols-3 gap-4 items-baseline">
      <div className="text-[10px] uppercase tracking-[0.24em] text-beige-100/50">
        {label}
      </div>
      <div className="col-span-2 font-serif text-lg tracking-tight break-words">
        {value}
      </div>
    </div>
  );
}
